import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Package } from '@lifeomic/chromicons';
import useEnforce from '@lifeomic/phc-web-toolkit/src/hooks/useEnforce';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import { permissions } from '@lifeomic/phc-web-toolkit/src/util/abac';
import {
  getKitSheetPatient,
  MONOSPACE_FONT,
  RqIdBarcodeArea,
  SPECIMEN,
} from './MyOmeKitSheet';
import {
  getMyOmeRequisitionCopy,
  getPlacedMyOmeRequisitionCopy,
  isMyOmeRequisitionFailed,
  isPlacedMyOmeRequisition,
  MyOmeRequisitionCopy,
} from './myOmeRequisitionState';
import {
  MyOmeRequisitionDetailsModal,
  useMyOmeRerun,
} from './MyOmeRequisitionDetailsModal';
import { useMemberDetailStyles } from './styles';
import { MyOmeRequisition, useMyOmeRequisition } from './useMyOmeRequisition';

// Loaded on click: printing renders with `react-dom/server`, which the member
// page doesn't otherwise need. The iframe prints from its own `load` handler,
// so this isn't tied to the click's user gesture.
const loadPrintMyOmeKitSheet = () =>
  import('./MyOmeKitSheet/printMyOmeKitSheet').then(
    module => module.printMyOmeKitSheet,
  );

const messages = defineMessages({
  title: {
    id: 'MemberDetail.myOmeRequisitionTile.title',
    defaultMessage: 'MyOme requisition',
  },
  iprsTitle: {
    id: 'MemberDetail.myOmeRequisitionTile.iprsTitle',
    defaultMessage: 'MyOme iPRS requisition',
  },
  loadError: {
    id: 'MemberDetail.myOmeRequisitionTile.loadError',
    defaultMessage: "Couldn't load MyOme requisition",
  },
  retry: {
    id: 'MemberDetail.myOmeRequisitionTile.retry',
    defaultMessage: 'Retry',
  },
  details: {
    id: 'MemberDetail.myOmeRequisitionTile.details',
    defaultMessage: 'Details',
  },
  printLoadError: {
    id: 'MemberDetail.myOmeRequisitionTile.printLoadError',
    defaultMessage: "Couldn't open the kit sheet. Try again.",
  },
  buccalHomeKit: {
    id: 'MemberDetail.myOmeRequisitionTile.buccalHomeKit',
    defaultMessage: 'Buccal swab, home kit',
  },
  placed: {
    id: 'MemberDetail.myOmeRequisitionTile.placed',
    defaultMessage: 'MyOme order placed. RQ id {rqId}.',
  },
});

const useStyles = makeStyles(theme => ({
  staticTile: {
    '&&': {
      cursor: 'default',
      '&:hover': { boxShadow: 'none' },
    },
  },
  rqId: {
    fontFamily: MONOSPACE_FONT,
    fontWeight: 600,
    overflowWrap: 'anywhere',
  },
  statusPill: {
    alignSelf: 'flex-start',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    padding: '1px 8px',
  },
  statusPillOnHold: {
    backgroundColor: theme.palette.orange[100],
    color: theme.palette.orange[800],
  },
  statusPillFailed: {
    backgroundColor: theme.palette.red[100],
    color: theme.palette.red[800],
  },
  statusPillPlaced: {
    backgroundColor: theme.palette.blue[100],
    color: theme.palette.blue[800],
  },
  trailing: {
    flexShrink: 0,
  },
}));

const AWAITING_STATUSES = new Set(['AWAITING_PARITY', 'AWAITING_GATES']);

/**
 * What the tile shows. A click always opens the details modal; a placed order
 * (`print` or `static`) shows MyOme's status, and only `print` offers Print
 * there.
 */
export type MyOmeRequisitionTileBehavior =
  | { kind: 'print'; rqId: string; copy: MyOmeRequisitionCopy }
  | {
      /** Nothing to print: MyOme mails a home kit, or iPRS reuses a sample. */
      kind: 'static';
      copy: MyOmeRequisitionCopy;
    }
  | { kind: 'details'; copy: MyOmeRequisitionCopy; failed: boolean };

export const getMyOmeRequisitionTileBehavior = (
  requisition: MyOmeRequisition,
): MyOmeRequisitionTileBehavior => {
  const { rqId, businessStatus } = requisition;
  if (!rqId || !isPlacedMyOmeRequisition(requisition)) {
    return {
      kind: 'details',
      copy: getMyOmeRequisitionCopy(requisition),
      failed: isMyOmeRequisitionFailed(businessStatus),
    };
  }
  const copy = getPlacedMyOmeRequisitionCopy(requisition);
  return requisition.kind === 'original' &&
    requisition.collection === 'blood-on-site'
    ? { kind: 'print', rqId, copy }
    : { kind: 'static', copy };
};

/**
 * Whether to offer Re-run. Tasks projected before GIS recorded `rerunnable`
 * fall back to AWAITING_*, where the route no-ops if it doesn't apply.
 */
export const canOfferMyOmeRerun = ({
  rerunnable,
  businessStatus,
}: MyOmeRequisition) =>
  rerunnable ?? AWAITING_STATUSES.has(businessStatus ?? '');

/**
 * Prints the patient's kit sheet with a given requisition area. Browsers
 * without a reliable frame `afterprint` would leave a "printing" lock stuck,
 * so a new print replaces any that hasn't opened yet; unmounting cancels too.
 */
const useKitSheetPrint = (patient: fhir3.Patient) => {
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const cancelPrintRef = useRef<() => void>();

  useEffect(() => () => cancelPrintRef.current?.(), []);

  return (requisitionArea: React.ReactNode) => {
    cancelPrintRef.current?.();
    let cancelled = false;
    let cancelOpenedPrint: (() => void) | undefined;
    cancelPrintRef.current = () => {
      cancelled = true;
      cancelOpenedPrint?.();
    };
    loadPrintMyOmeKitSheet()
      .then(printMyOmeKitSheet => {
        if (!cancelled) {
          cancelOpenedPrint = printMyOmeKitSheet({
            patient: getKitSheetPatient(patient),
            requisitionArea,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          notify(formatMessage(messages.printLoadError), 'error');
        }
      });
  };
};

type TileFrameProps = {
  title?: string;
  onClick?: () => void;
  trailing?: React.ReactNode;
  children?: React.ReactNode;
};

const TileFrame = ({ title, onClick, trailing, children }: TileFrameProps) => {
  const { formatMessage } = useIntl();
  const classes = useMemberDetailStyles();
  const ownClasses = useStyles();
  const content = (
    <>
      <div className={classes.landingCompactTileIconBadge}>
        <Package className={classes.landingCompactTileIcon} />
      </div>
      <div className={classes.landingCompactTileContent}>
        <Text size="subbody" weight="bold" className={classes.landingTileTitle}>
          {title ?? formatMessage(messages.title)}
        </Text>
        {children}
      </div>
      {trailing ? <div className={ownClasses.trailing}>{trailing}</div> : null}
    </>
  );
  const className = clsx(classes.landingTileBase, classes.landingCompactTile);
  return onClick ? (
    <button type="button" className={className} onClick={onClick}>
      {content}
    </button>
  ) : (
    <div
      role="group"
      aria-label={formatMessage(messages.title)}
      className={clsx(className, ownClasses.staticTile)}
    >
      {content}
    </div>
  );
};

type FoundTileProps = {
  requisition: MyOmeRequisition;
  patient: fhir3.Patient;
  onRefetch: () => void;
};

const FoundTile = ({ requisition, patient, onRefetch }: FoundTileProps) => {
  const { formatMessage } = useIntl();
  const classes = useMemberDetailStyles();
  const ownClasses = useStyles();
  const enforce = useEnforce();
  const [notify] = useNotify();
  const print = useKitSheetPrint(patient);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const behavior = getMyOmeRequisitionTileBehavior(requisition);
  const rerun = useMyOmeRerun(requisition, {
    refetch: onRefetch,
    onAnswered: response => {
      if (response.outcome === 'placed' && response.rqId) {
        notify(
          formatMessage(messages.placed, { rqId: response.rqId }),
          'success',
        );
      }
    },
  });
  const title = formatMessage(
    requisition.kind === 'iprs' ? messages.iprsTitle : messages.title,
  );
  const specimen =
    requisition.collection === 'buccal-home-kit'
      ? formatMessage(messages.buccalHomeKit)
      : SPECIMEN;

  // A refetch can move the requisition between placed and the details states;
  // the modal shouldn't reopen on its own when it does.
  useEffect(() => {
    setDetailsOpen(false);
  }, [behavior.kind]);

  return (
    <>
      <TileFrame
        title={title}
        onClick={() => setDetailsOpen(true)}
        trailing={
          <Text size="caption" className={classes.landingTileDescription}>
            {formatMessage(messages.details)}
          </Text>
        }
      >
        {requisition.rqId ? (
          <Text
            size="caption"
            className={clsx(classes.landingTileDescription, ownClasses.rqId)}
          >
            {requisition.rqId}
          </Text>
        ) : null}
        <span
          className={clsx(
            ownClasses.statusPill,
            behavior.kind !== 'details'
              ? ownClasses.statusPillPlaced
              : behavior.failed
              ? ownClasses.statusPillFailed
              : ownClasses.statusPillOnHold,
          )}
        >
          {behavior.copy.label}
        </span>
        <Text size="caption" className={classes.landingTileDescription}>
          {specimen}
        </Text>
      </TileFrame>
      {detailsOpen ? (
        <MyOmeRequisitionDetailsModal
          requisition={requisition}
          copy={behavior.copy}
          print={
            behavior.kind === 'print'
              ? () => {
                  setDetailsOpen(false);
                  print(<RqIdBarcodeArea rqId={behavior.rqId} />);
                }
              : undefined
          }
          rerun={
            canOfferMyOmeRerun(requisition) && enforce(permissions.createData)
              ? rerun.rerun
              : undefined
          }
          isRerunning={rerun.isRerunning}
          outcome={rerun.outcome}
          onClose={() => setDetailsOpen(false)}
        />
      ) : null}
    </>
  );
};

export type MyOmeRequisitionTileProps = {
  /** The unmasked Patient: MyOme matches the box by these identifiers. */
  patient: fhir3.Patient;
};

/**
 * The "MyOme requisition" tiles: one for the patient's latest at-center blood
 * requisition, the one place clinicians print its kit sheet, then one for the
 * latest buccal home kit, each followed by its iPRS order once placed. Each is
 * absent without a requisition Task of its kind, so the page looks as it did
 * before MyOme orders. A click opens the details modal, which offers Print
 * only for a placed blood order: never without an RQ id, or while on hold or
 * failed.
 */
export const MyOmeRequisitionTile = ({
  patient,
}: MyOmeRequisitionTileProps) => {
  const { formatMessage } = useIntl();
  const classes = useMemberDetailStyles();
  const result = useMyOmeRequisition(patient.id ?? '');

  switch (result.state) {
    case 'none':
      return null;
    case 'loading':
      return (
        <TileFrame>
          <DotLoader size={0} />
        </TileFrame>
      );
    case 'error':
      return (
        <TileFrame
          trailing={
            <Button variant="text" onClick={result.retry}>
              {formatMessage(messages.retry)}
            </Button>
          }
        >
          <Text size="caption" className={classes.landingTileDescription}>
            {formatMessage(messages.loadError)}
          </Text>
        </TileFrame>
      );
    case 'found':
      return (
        <>
          {result.requisitions.map(requisition => (
            <FoundTile
              key={requisition.taskId}
              requisition={requisition}
              patient={patient}
              onRefetch={result.refetch}
            />
          ))}
        </>
      );
  }
};
